import axios from "@axios"
import { makeRefNo, SCHOOL_NOTE_MARK } from "@/utils/helper"

// Ref# берём тот, что ввёл клиент, иначе генерим свой — пять цифр, не занятых
// среди уже поданных заявок этой фирмы (в базе офиса номер уникален в пределах
// фирма+Ref#+музей+дата). Раньше подставлялась метка времени в миллисекундах,
// 13 цифр, и в колонку «Ref#» шириной 5em она не влезала.
//
// rootGetters передаём из действия, а не импортируем хранилище: этот модуль сам
// часть хранилища, и импорт замкнул бы круг.
function refNoOrGenerated(entered, rootGetters) {
  const value = String(entered === undefined || entered === null ? "" : entered).trim()
  if (value !== "") return value

  const upcoming = (rootGetters && rootGetters["upcoming/getUpcomingList"]) || []

  return makeRefNo(upcoming.map(request => (request.data && request.data.refno) || ""))
}

export default {
  namespaced: true,
  state: {
    /**
     * is_sending - отправляется ли в данный момент заявка
     * is_show_message - показывать ли сообщение о результате отправки
     * request_is_success - успешно ли создана заявка
     * request_success_msg - текст сообщения о успешном создании заявки
     * request_error_msg - текст сообщения об ошибке
     */
    is_sending: false,
    is_show_message: false,
    request_is_success: null,
    request_success_msg: null,
    request_error_msg: null,
  },
  getters: {
    isSending: state => state.is_sending,
    isShowMessage: state => state.is_show_message,
    successMsg: state => state.request_success_msg,
    errorMsg: state => state.request_error_msg,
    isSuccess: state => state.request_is_success,
  },
  mutations: {
    start_order_sending(state) {
      state.is_sending = true

      // Сбрасываем результат прошлой отправки — иначе номера заявок копятся между
      // отправками. Накопление нужно только ВНУТРИ одной отправки (РГ/АГ, график,
      // транспорт создают несколько заявок за раз и коммитят success по каждой).
      state.request_success_msg = null
      state.request_error_msg = null
    },
    set_on_request_success(state, id) {
      // Заявка ушла — на «Заказах» предложим оценить кабинет. Метка живёт до
      // закрытия вкладки; окно покажется, только если клиент отзыв ещё не
      // оставлял (это проверяет сервер).
      try {
        sessionStorage.setItem('askFeedback', '1')
      } catch (e) {
        // приватный режим браузера — не беда, просто не спросим
      }
      if (state.request_success_msg === null) {
        state.request_success_msg = `№${id}`
      } else {
        state.request_success_msg += `, №${id}`
      }
      state.request_is_success = true
      state.is_sending = false
      state.is_show_message = true
      state.request_error_msg = null
    },
    close_message(state) {
      state.is_show_message = false
    },
    set_on_request_error(state, error) {
      state.request_is_success = false
      state.is_sending = false
      state.is_show_message = true
      state.request_error_msg = error
    },
  },
  actions: {
    createOrder({ commit, rootGetters }, formData) {
      commit("start_order_sending")

      return new Promise((resolve, reject) => {
        const data = JSON.stringify({
          refno: refNoOrGenerated(formData.value.refNumber, rootGetters),
          museum_id: formData.value.museum,
          system_id: formData.value.system_id,
          pass_date: formData.value.date,
          schedule_item_id: formData.value.schedule,
          persons_per_guide: formData.value.persons_per_guide,
          country_id: formData.value.country,
          guide: formData.value.guideName,
          guide_phone: formData.value.guidePhone,
          tickets: formData.value.tickets,
          services: formData.value.services,
          qty_guide: formData.value.guides,

          // Служебные пометки — в начало примечаний (колонка «Примечания» в
          // офисе узкая): школьная группа (цены по школьному прайс-листу) и
          // заявка на дату, закрытую в календаре (выходной/праздничный), чтобы
          // оператор видел, что экскурсия идёт не в обычный день.
          notes: [
            formData.value.school ? SCHOOL_NOTE_MARK : '',
            formData.value.closedDay ? 'По спец прайсу.' : '',
            String(formData.value.comment || '').trim(),
          ].filter(Boolean).join(' '),
          type: formData.value.type,
        })
        const postData = new FormData()
        postData.append("data", data)
        postData.append("action", "post_request")
        axios({
          method: "post",
          url: "/api/v1/actions/",
          data: postData,
          headers: { "Content-Type": "multipart/form-data" },
        })
          .then(response => {
            if (response.status === 200) {
              if (response.data.success) {
                commit("set_on_request_success", response.data.data.result.id)
                resolve(response)
              } else {
                commit("set_on_request_error", response.data.error)
                resolve(response)
              }
            }
          })
          .catch(error => {
            reject(error)
          })
      })
    },

    // Расписание на дату БЕЗ записи в общее хранилище: нужно групповой заявке,
    // чтобы перед отправкой проверить каждый музей и при этом не затереть
    // расписание, которое сейчас показано в форме.
    checkSchedule(_context, formData) {
      const data = JSON.stringify({
        museum_object_id: formData.museumObjectId,
        schedule_date: formData.date,
      })
      const postData = new FormData()
      postData.append("data", data)
      postData.append("action", "get_schedule")

      return axios({
        method: "post",
        url: "/api/v1/actions/",
        data: postData,
        headers: { "Content-Type": "multipart/form-data" },
      })
        .then(response => {
          const payload = response.data || {}
          if (!payload.success) {
            return { ok: false, error: payload.error || "Не удалось получить расписание", schedule: [] }
          }

          return {
            ok: true,
            error: null,
            schedule: (((payload.data || {}).schedule_data || {}).result) || [],
          }
        })
        .catch(() => ({ ok: false, error: "Не удалось получить расписание", schedule: [] }))
    },

    // Групповая заявка «несколько музеев на одну группу»: чисто клиентское
    // удобство — в офис уходит N ОБЫЧНЫХ заявок (post_request), как если бы фирма
    // подала их по одной. Общая метка «ГРУППОВАЯ ЗАЯВКА №код» в примечании каждой
    // показывает оператору, что это один заезд тургруппы.
    // Шлём ПОСЛЕДОВАТЕЛЬНО, а не Promise.all: система заявок хрупкая, пачку
    // параллельных запросов лучше не устраивать. На первой же ошибке
    // ОСТАНАВЛИВАЕМСЯ и оставшиеся музеи не отправляем: клиенту нужна либо вся
    // группа целиком, либо понятная причина отказа, а не половина заезда в офисе.
    async createGroupOrder({ rootGetters }, entries) {
      const results = []
      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i]
        const data = JSON.stringify({
          refno: refNoOrGenerated(entry.refNumber, rootGetters),
          museum_id: entry.museum,
          system_id: entry.system_id,
          pass_date: entry.date,
          schedule_item_id: entry.schedule,
          persons_per_guide: entry.persons_per_guide,
          country_id: entry.country,
          guide: entry.guide,
          guide_phone: entry.guidePhone,
          tickets: entry.tickets,
          services: entry.services,
          qty_guide: entry.guides,
          notes: entry.notes,
          type: entry.type,
        })
        const postData = new FormData()
        postData.append("data", data)
        postData.append("action", "post_request")

        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await axios({
            method: "post",
            url: "/api/v1/actions/",
            data: postData,
            headers: { "Content-Type": "multipart/form-data" },
          })
          if (response.status === 200 && response.data.success) {
            results.push({ index: i, ok: true, id: response.data.data.result.id })
          } else {
            results.push({ index: i, ok: false, error: (response.data && response.data.error) || "Ошибка отправки" })
            break
          }
        } catch (error) {
          results.push({ index: i, ok: false, error: "Система заявок не ответила" })
          break
        }
      }

      return results
    },

    // То же самое для радиогида/аудиогида: N заявок post_request_rg_ag на один
    // заезд, последовательно, со стопом на первой ошибке.
    async createGroupOrderRgAg({ rootGetters }, entries) {
      const results = []
      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i]
        const data = JSON.stringify({
          refno: refNoOrGenerated(entry.refNumber, rootGetters),
          museum_id: entry.museum,
          system_id: entry.museum,
          pass_date: entry.date,
          schedule_item_id: entry.schedule,
          persons_per_guide: entry.persons_per_guide,
          country_id: 100,
          guide: entry.guide,
          guide_phone: entry.guidePhone,
          tickets: entry.tickets,
          qty_guide: entry.guides,
          notes: entry.notes,
          type: entry.type,
        })
        const postData = new FormData()
        postData.append("data", data)
        postData.append("action", "post_request_rg_ag")

        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await axios({
            method: "post",
            url: "/api/v1/actions/",
            data: postData,
            headers: { "Content-Type": "multipart/form-data" },
          })
          if (response.status === 200 && response.data.success) {
            results.push({ index: i, ok: true, id: response.data.data.result.id })
          } else {
            results.push({ index: i, ok: false, error: (response.data && response.data.error) || "Ошибка отправки" })
            break
          }
        } catch (error) {
          results.push({ index: i, ok: false, error: "Система заявок не ответила" })
          break
        }
      }

      return results
    },
    createOrderForRgAg({ commit, rootGetters }, formDataArray) {
      commit("start_order_sending")

      // Function to process each formData item
      const processFormData = formData => {
        const refno = refNoOrGenerated(formData.refNumber, rootGetters)
        let fullNote = ""
        if (formData.receiving !== "") {
          fullNote += `Получение: ${formData.receiving}; `
        }
        if (formData.returning !== "") {
          fullNote += `Возврат: ${formData.returning}; `
        }
        if (formData.deliveryZone === "in") {
          fullNote += "Доставка в пределах КАД; "
        }
        if (formData.deliveryZone === "out") {
          fullNote += "Доставка за пределы КАД; "
        }
        if (formData.comment !== "") {
          fullNote += `${formData.comment}`
        }

        const data = JSON.stringify({
          refno,
          museum_id: formData.museum,
          system_id: formData.museum,
          pass_date: formData.date,
          schedule_item_id: formData.schedule,
          persons_per_guide: formData.persons_per_guide,
          country_id: 100,
          guide: formData.guideName,
          guide_phone: formData.guidePhone,
          tickets: formData.tickets,
          qty_guide: formData.guides,
          notes: fullNote,
          type: formData.type,
        })

        const postData = new FormData()
        postData.append("data", data)
        postData.append("action", "post_request_rg_ag")

        return axios({
          method: "post",
          url: "/api/v1/actions/",
          data: postData,
          headers: { "Content-Type": "multipart/form-data" },
        })
      }

      // Process all form data items
      return Promise.all(formDataArray.map(item => processFormData(item)))
        .then(responses => {
          responses.forEach(response => {
            if (response.status === 200) {
              if (response.data.success) {
                commit("set_on_request_success", response.data.data.result.id)
              } else {
                commit("set_on_request_error", response.data.error)
              }
            }
          })

          return responses
        })
        .catch(error => {
          // Handle the error appropriately
          console.error("An error occurred:", error)
          throw error
        })
    },
    createOrderForSheduleRequest({ commit, rootGetters }, formDataArray) {
      commit("start_order_sending")
      console.log(formDataArray)

      const processFormData = formData => {
        let countryId = 267
        let fullNote = "ГРАФИК. "
        if (formData.groupType === 'en') {
            // иностранные туристы: страну выбирает клиент в форме графика
            countryId = formData.country
        }
        if (formData.groupType === 'cn') {
            countryId = 100
            if (formData.services.ag === true) {
                fullNote += "АГ: Да, "
            } else {
                fullNote += "АГ: Нет, "
            }
            if (formData.services.rg === true) {
                fullNote += "РГ: Да, "
            } else {
                fullNote += "РГ: Нет, "
            }
            fullNote += `Гид c лицензией: ${formData.services.license}, `
            fullNote += `Сопровождающий: ${formData.services.sopr}. `
        }

        const data = JSON.stringify({
          refno: refNoOrGenerated(formData.refNumber, rootGetters),
          museum_id: formData.museum,
          system_id: formData.museum,
          pass_date: formData.date,
          schedule_item_id: formData.schedule,
          persons_per_guide: formData.persons_per_guide,
          country_id: countryId,
          guide: formData.guideName,
          guide_phone: formData.guidePhone,
          adult: formData.tickets.adult,
          child: formData.tickets.child,
          qty_guide: formData.guides,
          notes: fullNote,
        })

        const postData = new FormData()
        postData.append("data", data)
        postData.append("action", "post_request_schedule")

        return axios({
          method: "post",
          url: "/api/v1/actions/",
          data: postData,
          headers: { "Content-Type": "multipart/form-data" },
        })
      }

      // Process all form data items
      return Promise.all(formDataArray.map(item => processFormData(item)))
        .then(responses => {
          responses.forEach(response => {
            if (response.status === 200) {
              if (response.data.success) {
                console.log(response.data.data.result.id)
                commit("set_on_request_success", response.data.data.result.id)
              } else {
                commit("set_on_request_error", response.data.error)
              }
            }
          })

          return responses
        })
        .catch(error => {
          // Handle the error appropriately
          console.error("An error occurred:", error)
          throw error
        })
    },
    createOrderForTransportRequest({ commit, rootGetters }, formDataArray) {
      commit("start_order_sending")

      const processFormData = formData => {
        // console.log('formData', formData)
        const countryId = 100
        let fullNote = "Заявка на транспорт. "
        fullNote += `Время начала: ${formData.startTime}, `
        fullNote += `Время окончания: ${formData.endTime}, `
        fullNote += `Адрес начала: ${formData.startAddress}, `
        fullNote += `Адрес окончания: ${formData.endPlace}, `
        fullNote += `Описание маршрута: ${formData.routeDescription}.`

        const data = JSON.stringify({
          refno: refNoOrGenerated("", rootGetters),
          museum_id: formData.transportType,
          system_id: formData.transportType,
          pass_date: formData.date,
          schedule_item_id: formData.scheduleId,
          persons_per_guide: formData.persons_per_guide,
          country_id: countryId,
          guide: formData.guideName,
          guide_phone: formData.guidePhone,
          adult: formData.tickets.adult,
          child: formData.tickets.child,
          qty_guide: '0',
          notes: fullNote,
        })

        const postData = new FormData()
        console.log('data', data)
        postData.append("data", data)
        postData.append("action", "post_request_transport")

        return axios({
          method: "post",
          url: "/api/v1/actions/",
          data: postData,
          headers: { "Content-Type": "multipart/form-data" },
        })
      }

      // Process all form data items
      return Promise.all(formDataArray.map(item => processFormData(item)))
        .then(responses => {
          responses.forEach(response => {
            if (response.status === 200) {
              if (response.data.success) {
                console.log(response.data.data.result.id)
                commit("set_on_request_success", response.data.data.result.id)
              } else {
                commit("set_on_request_error", response.data.error)
              }
            }
          })

          return responses
        })
        .catch(error => {
          // Handle the error appropriately
          console.error("An error occurred:", error)
          throw error
        })
    },
    createOrderForGuideRequest({ commit, rootGetters }, formDataArray) {
      commit("start_order_sending")

      const processFormData = formData => {
        const countryId = 100
        let fullNote = "Заявка на гида. "
        fullNote += `Время встречи: ${formData.startTime}, `
        fullNote += `Время окончания: ${formData.finishTime}, `
        fullNote += `Место встречи: ${formData.startAddress}, `
        fullNote += `Место окончания: ${formData.finishAddress}, `
        fullNote += `Пожелания: ${formData.notes}.`

        const data = JSON.stringify({
          refno: refNoOrGenerated("", rootGetters),
          museum_id: formData.language,
          system_id: formData.language,
          pass_date: formData.date,
          schedule_item_id: formData.scheduleId,
          persons_per_guide: formData.persons_per_guide,
          country_id: countryId,
          guide: formData.guideName,
          guide_phone: formData.guidePhone,
          adult: formData.tickets.adult,
          child: formData.tickets.child,
          qty_guide: '0',
          notes: fullNote,
        })

        const postData = new FormData()
        console.log('data', data)
        postData.append("data", data)
        postData.append("action", "post_request_guide")

        return axios({
          method: "post",
          url: "/api/v1/actions/",
          data: postData,
          headers: { "Content-Type": "multipart/form-data" },
        })
      }

      // Process all form data items
      return Promise.all(formDataArray.map(item => processFormData(item)))
        .then(responses => {
          responses.forEach(response => {
            if (response.status === 200) {
              if (response.data.success) {
                console.log(response.data.data.result.id)
                commit("set_on_request_success", response.data.data.result.id)
              } else {
                commit("set_on_request_error", response.data.error)
              }
            }
          })

          return responses
        })
        .catch(error => {
          // Handle the error appropriately
          console.error("An error occurred:", error)
          throw error
        })
    },
  },
}
